<template>
  <div class="committee-form-panel">
    <!-- Header -->
    <div class="form-header p-3">
      <div>
        <h4 class="mb-0">{{ isEditing ? "Editar Comitê" : "Novo Comitê" }}</h4>
      </div>
    </div>

    <!-- Body -->
    <div class="form-body p-3">
      <b-form @submit.prevent="handleSubmit">
        <div class="section-card mb-4">
          <h6 class="section-title">Informações do Comitê</h6>
          <b-form-group label="Nome do Comitê" label-for="committee-name">
            <b-form-input
              id="committee-name"
              v-model="form.name"
              required
              placeholder="Digite o nome do comitê"
            ></b-form-input>
          </b-form-group>
        </div>

        <div class="section-card mb-4">
          <h6 class="section-title">Membros do Comitê</h6>
          <b-form-group
            label="Selecione os usuários que farão parte do comitê"
            label-for="committee-users"
          >
            <div class="user-selection">
              <div
                v-for="user in companyUsers"
                :key="user._id"
                class="user-item"
              >
                <b-form-checkbox
                  v-model="form.users"
                  :value="user._id"
                  class="user-checkbox"
                >
                  <div class="user-info">
                    <i class="fas fa-user mr-2"></i>
                    <span>{{ user.name }} {{ user.surname }}</span>
                    <small class="text-muted d-block">{{ user.email }}</small>
                  </div>
                </b-form-checkbox>
              </div>
            </div>
          </b-form-group>
        </div>

        <!-- Histórico do Comitê (apenas na edição) -->
        <div
          v-if="isEditing && events && events.length"
          class="section-card mb-4"
        >
          <h6 class="section-title">Histórico de Alterações</h6>
          <div class="history-list">
            <div v-for="event in events" :key="event._id" class="history-item">
              <div class="d-flex w-100 justify-content-between">
                <h6 class="mb-1 history-title">
                  #{{ event.number }} - {{ getEventTypeText(event.type) }}
                </h6>
                <small class="history-date">{{
                  event.createdAt | datetime
                }}</small>
              </div>
              <div class="history-details mt-1">
                <p class="mb-1 history-description">
                  {{ event.description }}
                </p>
                <div v-if="event.user" class="history-user">
                  <small class="text-muted">
                    Por: {{ event.user.name }} {{ event.user.surname }}
                  </small>
                </div>

                <!-- Detalhes específicos por tipo de evento -->
                <div
                  v-if="event.type === 2 && event.data"
                  class="history-changes mt-2"
                >
                  <div
                    v-if="event.data.oldName !== event.data.newName"
                    class="change-item"
                  >
                    <small class="text-muted">
                      <strong>Nome:</strong> "{{ event.data.oldName }}" → "{{
                        event.data.newName
                      }}"
                    </small>
                  </div>

                  <div
                    v-if="event.data.addedUsers && event.data.addedUsers.length"
                    class="change-item"
                  >
                    <small class="text-success">
                      <strong>Membros adicionados:</strong>
                      <span
                        v-for="(user, index) in event.data.addedUsers"
                        :key="user._id"
                      >
                        {{ user.name }} {{ user.surname
                        }}<span v-if="index < event.data.addedUsers.length - 1"
                          >,
                        </span>
                      </span>
                    </small>
                  </div>

                  <div
                    v-if="
                      event.data.removedUsers && event.data.removedUsers.length
                    "
                    class="change-item"
                  >
                    <small class="text-danger">
                      <strong>Membros removidos:</strong>
                      <span
                        v-for="(user, index) in event.data.removedUsers"
                        :key="user._id"
                      >
                        {{ user.name }} {{ user.surname
                        }}<span
                          v-if="index < event.data.removedUsers.length - 1"
                          >,
                        </span>
                      </span>
                    </small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </b-form>
    </div>

    <!-- Footer -->
    <div class="form-footer p-3">
      <b-button
        variant="primary"
        class="w-100 mb-2"
        :disabled="!form.name || form.users.length === 0"
        @click="handleSubmit"
      >
        {{ isEditing ? "Atualizar Comitê" : "Criar Comitê" }}
      </b-button>
      <b-button
        variant="outline-secondary"
        class="w-100 mb-2"
        @click="$emit('close')"
      >
        Cancelar
      </b-button>
      <b-button
        v-if="isEditing"
        variant="outline-danger"
        class="w-100"
        @click="confirmDelete"
      >
        <i class="fas fa-trash mr-2"></i>
        Excluir Comitê
      </b-button>
    </div>
  </div>
</template>

<script>
import { getCompanyUsers } from "@/api/company";
import { getCommitteeEvents } from "@/api/committee";
import { mapGetters } from "vuex";

export default {
  name: "CommitteeForm",
  props: {
    committee: {
      type: Object,
      default: null,
    },
    companyId: {
      type: [String, Number],
      default: null,
    },
  },
  data() {
    return {
      form: {
        name: "",
        users: [],
      },
      companyUsers: [],
      events: [],
    };
  },
  computed: {
    ...mapGetters(["profile"]),
    isEditing() {
      return !!this.committee;
    },
  },
  watch: {
    committee: {
      async handler(newCommittee) {
        if (newCommittee) {
          this.form.name = newCommittee.name;
          this.form.users = newCommittee.users.map((user) => user._id || user);
          await this.loadEvents();
        } else {
          this.form.name = "";
          this.form.users = [];
          this.events = [];
        }
      },
      immediate: true,
    },
  },
  async created() {
    await this.loadCompanyUsers();
    if (this.committee) {
      this.form.name = this.committee.name;
      this.form.users = this.committee.users.map((user) => user._id || user);
      await this.loadEvents();
    }
  },
  methods: {
    async loadCompanyUsers() {
      try {
        const id =
          this.companyId ||
          (this.committee
            ? this.committee.company._id || this.committee.company
            : this.profile.company);
        const response = await getCompanyUsers(id, true);
        this.companyUsers = response.data;
      } catch (error) {
        this.$bvToast.toast(
          "Não foi possível carregar os usuários da empresa.",
          {
            title: "Erro",
            toastClass: "custom-toast custom-toast-error",
            variant: "light",
            solid: true,
            autoHideDelay: 10000,
            noCloseButton: false,
          }
        );
      }
    },
    handleSubmit() {
      if (!this.form.name || this.form.users.length === 0) {
        this.$bvToast.toast(
          "Preencha o nome do comitê e selecione pelo menos um membro.",
          {
            title: "Atenção",
            toastClass: "custom-toast custom-toast-warning",
            variant: "light",
            solid: true,
            autoHideDelay: 8000,
            noCloseButton: false,
          }
        );
        return;
      }

      this.$emit("submit", {
        ...this.form,
        id: this.committee ? this.committee._id : null,
      });
    },
    async loadEvents() {
      try {
        const response = await getCommitteeEvents(this.committee._id);
        this.events = response.data;
      } catch (error) {
        this.$bvToast.toast(
          "Não foi possível carregar o histórico do comitê.",
          {
            title: "Erro",
            toastClass: "custom-toast custom-toast-error",
            variant: "light",
            solid: true,
            autoHideDelay: 10000,
            noCloseButton: false,
          }
        );
      }
    },
    getEventTypeText(type) {
      const types = {
        1: "Criação",
        2: "Atualização",
        3: "Exclusão",
      };
      return types[type] || "Evento";
    },
    confirmDelete() {
      this.$bvModal
        .msgBoxConfirm(
          `Tem certeza que deseja excluir o comitê "${this.committee.name}"?`,
          {
            title: "Confirmar Exclusão",
            size: "sm",
            buttonSize: "sm",
            okVariant: "danger",
            okTitle: "Excluir",
            cancelTitle: "Cancelar",
            footerClass: "p-2",
            hideHeaderClose: false,
            centered: true,
          }
        )
        .then((value) => {
          if (value) {
            this.$emit("delete", this.committee);
          }
        });
    },
  },
};
</script>

<style scoped>
.committee-form-panel {
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: #f7f9fc;
}

.form-header {
  background-color: #ffffff;
  border-bottom: 1px solid #e9ecef;
}

.form-body {
  flex-grow: 1;
  overflow-y: auto;
}

.section-card {
  background-color: #fff;
  padding: 1.25rem;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.section-title {
  font-weight: 600;
  margin-bottom: 1rem;
  color: #0056b3;
}

.user-selection {
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 0.5rem;
}

.user-item {
  padding: 0.5rem;
  border-bottom: 1px solid #f8f9fa;
}

.user-item:last-child {
  border-bottom: none;
}

.user-checkbox {
  margin-bottom: 0;
}

.user-info {
  display: flex;
  flex-direction: column;
  margin-left: 0.5rem;
}

.form-footer {
  background-color: #ffffff;
  border-top: 1px solid #e9ecef;
  box-shadow: 0 -2px 4px rgba(0, 0, 0, 0.05);
}

.history-list {
  max-height: 300px;
  overflow-y: auto;
}

.history-item {
  padding: 0.75rem;
  border-bottom: 1px solid #e9ecef;
  margin-bottom: 0.5rem;
}

.history-item:last-child {
  border-bottom: none;
  margin-bottom: 0;
}

.history-title {
  color: #495057;
  font-size: 0.9rem;
}

.history-date {
  color: #6c757d;
}

.history-description {
  color: #6c757d;
  font-size: 0.85rem;
}

.history-user {
  margin-top: 0.25rem;
}

.history-changes {
  background-color: #f8f9fa;
  padding: 0.5rem;
  border-radius: 4px;
  border-left: 3px solid #007bff;
}

.change-item {
  display: block;
  margin-bottom: 0.25rem;
}

.change-item:last-child {
  margin-bottom: 0;
}
</style>
